// src/screens/SalaAsambleaScreen.js
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { db } from '../firebaseConfig';
import { 
  doc, 
  getDoc, 
  getDocs,
  updateDoc, 
  collection, 
  addDoc, 
  onSnapshot, 
  query, 
  where,
  orderBy, 
  serverTimestamp, 
  setDoc,
  deleteDoc,
  increment 
} from 'firebase/firestore';
import { 
  IoArrowBack, 
  IoVideocamOutline, 
  IoVideocamOffOutline, 
  IoMicOutline, 
  IoMicOffOutline, 
  IoPeopleOutline, 
  IoChatbubblesOutline, 
  IoHandRightOutline, 
  IoLockClosedOutline, 
  IoLockOpenOutline, 
  IoCheckmarkCircleOutline, 
  IoCloseCircleOutline, 
  IoSend, 
  IoDocumentTextOutline,
  IoStatsChartOutline,
  IoClose,
  IoCheckmark,
  IoRefreshOutline,
  IoVolumeHighOutline,
  IoVolumeMuteOutline
} from 'react-icons/io5';
import './AsambleasScreen.css';

// Configuración STUN pública de Google para conexión WebRTC P2P de baja latencia
const RTC_CONFIG = {
  iceServers: [
    { urls: 'stun:stun.l.google.com:19302' },
    { urls: 'stun:stun1.l.google.com:19302' },
    { urls: 'stun:stun2.l.google.com:19302' },
    { urls: 'stun:stun3.l.google.com:19302' },
    { urls: 'stun:stun4.l.google.com:19302' }
  ],
  iceCandidatePoolSize: 10
};

function SalaAsambleaScreen() {
  const { asambleaId } = useParams();
  const navigate = useNavigate();
  const { userData, currentUser, isMoroso } = useAuth();

  const [asamblea, setAsamblea] = useState(null);
  const [loading, setLoading] = useState(true);
  const [messages, setMessages] = useState([]);
  const [inputMsg, setInputMsg] = useState('');
  
  // Pestañas móviles (para despejar la cámara en pantallas pequeñas)
  const [mobileTab, setMobileTab] = useState('video'); // 'video' | 'chat'

  // Votaciones en vivo
  const [activeVotacion, setActiveVotacion] = useState(null);
  const [hasVoted, setHasVoted] = useState(false);
  const [userVoteOption, setUserVoteOption] = useState(null);
  const [secondsLeft, setSecondsLeft] = useState(60);
  const [showVotacionModal, setShowVotacionModal] = useState(false);
  const [nuevaPregunta, setNuevaPregunta] = useState('');
  
  // Manos levantadas (Pedir la palabra)
  const [handRaised, setHandRaised] = useState(false);
  const [handsList, setHandsList] = useState([]);
  const [showHandsDropdown, setShowHandsDropdown] = useState(false);

  // Minuta y conclusión
  const [isFinishing, setIsFinishing] = useState(false);
  const [minutaGenerada, setMinutaGenerada] = useState(null);
  const [votacionesHistorialFinal, setVotacionesHistorialFinal] = useState([]);

  // Cámara / Audio Local para el Administrador
  const [isStreaming, setIsStreaming] = useState(false);
  const [isMicMuted, setIsMicMuted] = useState(false);
  const [isVideoMuted, setIsVideoMuted] = useState(false);
  const [cameraFacing, setCameraFacing] = useState('user');
  const [cameraError, setCameraError] = useState(null);
  const [micLevel, setMicLevel] = useState(0); // 0 a 100% de intensidad de voz
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  // Video remoto para Residentes (WebRTC + Live Frame Relay)
  const remoteVideoRef = useRef(null);
  const remoteAudioRef = useRef(null);
  const remoteStreamRef = useRef(null);
  const spectatorAudioContextRef = useRef(null);
  const peerConnectionRef = useRef(null);
  const [isRemoteVideoLive, setIsRemoteVideoLive] = useState(false);
  const [liveFrame, setLiveFrame] = useState(null);
  const [isMuted, setIsMuted] = useState(true);
  const [isPaused, setIsPaused] = useState(false);
  const [otraAsambleaEnVivo, setOtraAsambleaEnVivo] = useState(null);
  const [sentAudioChunks, setSentAudioChunks] = useState(0);
  const [receivedAudioChunks, setReceivedAudioChunks] = useState(0);

  const isAdmin = ['admin', 'comite', 'superuser', 'masteruser'].includes((userData?.rol || '').toLowerCase());
  const esPropietario = (userData?.tipoResidente || '').toLowerCase() === 'propietario' || userData?.rol === 'admin';
  const tieneDerechoVoto = esPropietario && !isMoroso;

  // 1. Cargar datos de la Asamblea
  useEffect(() => {
    if (!asambleaId) return;

    const asambleaRef = doc(db, 'asambleas', asambleaId);
    const unsubscribe = onSnapshot(asambleaRef, (snap) => {
      if (snap.exists()) {
        setAsamblea({ id: snap.id, ...snap.data() });
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [asambleaId]);

  // 1.1 Detectar si hay otra asamblea transmitiendo en vivo en el condominio
  useEffect(() => {
    if (!userData?.condominioId) return;

    const q = query(
      collection(db, 'asambleas'),
      where('condominioId', '==', userData.condominioId),
      where('estatus', '==', 'en_vivo')
    );
    const unsubscribe = onSnapshot(q, (snap) => {
      const encontrada = snap.docs.find(d => d.id !== asambleaId);
      if (encontrada) {
        setOtraAsambleaEnVivo({ id: encontrada.id, ...encontrada.data() });
      } else {
        setOtraAsambleaEnVivo(null);
      }
    });

    return () => unsubscribe();
  }, [userData?.condominioId, asambleaId]);

  // 2. Registrar asistencia para cálculo de Quórum
  useEffect(() => {
    if (!asambleaId || !currentUser || !userData) return;

    const asistenciaRef = doc(db, `asambleas/${asambleaId}/asistencia`, currentUser.uid);
    setDoc(asistenciaRef, {
      residenteId: currentUser.uid,
      nombre: userData.nombre || 'Residente',
      casa: userData.casa || userData.numeroCasa || 'S/N',
      rol: userData.rol || 'residente',
      tipoResidente: userData.tipoResidente || 'inquilino',
      isMoroso: !!isMoroso,
      tieneDerechoVoto,
      indiviso: Number(userData.indiviso) || 1.0,
      conectadoAt: serverTimestamp()
    }, { merge: true });
  }, [asambleaId, currentUser, userData, isMoroso, tieneDerechoVoto]);

  // 3. Escuchar Quórum en vivo
  useEffect(() => {
    if (!asambleaId) return;

    const asistenciaCol = collection(db, `asambleas/${asambleaId}/asistencia`);
    const unsubscribe = onSnapshot(asistenciaCol, (snap) => {
      const totalAsistentes = snap.size;
      const quorumEstimado = Math.min(100, Math.round((totalAsistentes / 50) * 100)); // Base 50 casas
      if (isAdmin && asamblea && asamblea.quorumActual !== quorumEstimado) {
        updateDoc(doc(db, 'asambleas', asambleaId), { quorumActual: quorumEstimado }).catch(() => {});
      }
    });

    return () => unsubscribe();
  }, [asambleaId, isAdmin, asamblea]);

  // 4. Escuchar Chat en vivo
  useEffect(() => {
    if (!asambleaId) return;

    const chatCol = query(collection(db, `asambleas/${asambleaId}/chat`), orderBy('createdAt', 'asc'));
    const unsubscribe = onSnapshot(chatCol, (snap) => {
      setMessages(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });

    return () => unsubscribe();
  }, [asambleaId]);

  // 5. Escuchar Votaciones en vivo activas
  useEffect(() => {
    if (!asambleaId) return;

    const votacionesCol = collection(db, `asambleas/${asambleaId}/votaciones`);
    const unsubscribe = onSnapshot(votacionesCol, (snap) => {
      const activa = snap.docs.find(d => d.data().activa === true);
      if (activa) {
        setActiveVotacion({ id: activa.id, ...activa.data() });
      } else {
        setActiveVotacion(null);
        setHasVoted(false);
        setUserVoteOption(null);
      }
    });

    return () => unsubscribe();
  }, [asambleaId]);

  // 6. Escuchar si el usuario ya votó en la votación activa
  useEffect(() => {
    if (!activeVotacion || !currentUser) {
      setHasVoted(false);
      setUserVoteOption(null);
      return;
    }

    const votoRef = doc(db, `asambleas/${asambleaId}/votaciones/${activeVotacion.id}/votos`, currentUser.uid);
    const unsubscribe = onSnapshot(votoRef, (snap) => {
      if (snap.exists()) {
        setHasVoted(true);
        setUserVoteOption(snap.data()?.opcion);
      } else {
        setHasVoted(false);
        setUserVoteOption(null);
      }
    });

    return () => unsubscribe();
  }, [activeVotacion?.id, asambleaId, currentUser]);

  // 7. Cronómetro regresivo para votación (60 segundos FIJOS sin reinicio por votos)
  useEffect(() => {
    if (!activeVotacion?.id) return;

    const calcSeconds = () => {
      if (activeVotacion.expiraAt) {
        const nowMs = Date.now();
        const diff = Math.max(0, Math.ceil((activeVotacion.expiraAt - nowMs) / 1000));
        return diff;
      }
      return 60;
    };

    setSecondsLeft(calcSeconds());

    const interval = setInterval(() => {
      const remaining = calcSeconds();
      setSecondsLeft(remaining);
      if (remaining <= 0) {
        clearInterval(interval);
        if (isAdmin) {
          updateDoc(doc(db, `asambleas/${asambleaId}/votaciones`, activeVotacion.id), { activa: false }).catch(() => {});
        }
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [activeVotacion?.id, isAdmin, asambleaId]);

  // 8. Escuchar Manos Levantadas en vivo
  useEffect(() => {
    if (!asambleaId) return;

    const manosCol = collection(db, `asambleas/${asambleaId}/manos_levantadas`);
    const unsubscribe = onSnapshot(manosCol, (snap) => {
      const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setHandsList(list);
      if (currentUser) {
        setHandRaised(list.some(m => m.id === currentUser.uid || m.uid === currentUser.uid));
      }
    });

    return () => unsubscribe();
  }, [asambleaId, currentUser]);

  // 9. MEDIDOR DE INTENSIDAD DE MICRÓFONO (Web Audio API Analyser estilo Google Meet/Teams)
  useEffect(() => {
    if (!isStreaming || isMicMuted || !streamRef.current) {
      setMicLevel(0);
      return;
    }

    let audioContext;
    let analyser;
    let microphone;
    let animationFrameId;

    try {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      if (!AudioCtx) return;

      audioContext = new AudioCtx();
      analyser = audioContext.createAnalyser();
      analyser.fftSize = 256;
      analyser.smoothingTimeConstant = 0.4;

      const audioTrack = streamRef.current.getAudioTracks()[0];
      if (audioTrack && audioTrack.enabled) {
        microphone = audioContext.createMediaStreamSource(streamRef.current);
        microphone.connect(analyser);

        const dataArray = new Uint8Array(analyser.frequencyBinCount);

        const checkVolume = () => {
          if (!analyser) return;
          analyser.getByteFrequencyData(dataArray);
          let sum = 0;
          for (let i = 0; i < dataArray.length; i++) {
            sum += dataArray[i];
          }
          const avg = sum / dataArray.length;
          // Normalizar a escala de 0 a 100
          const level = Math.min(100, Math.round((avg / 128) * 100));
          setMicLevel(level);

          animationFrameId = requestAnimationFrame(checkVolume);
        };

        checkVolume();
      }
    } catch (err) {
      console.warn("AudioContext error:", err);
    }

    return () => {
      if (animationFrameId) cancelAnimationFrame(animationFrameId);
      if (audioContext && audioContext.state !== 'closed') {
        audioContext.close().catch(() => {});
      }
    };
  }, [isStreaming, isMicMuted]);

  // 10. LIVE FRAME RELAY (Garantía Visual Inmediata)
  // Administrador emisor: Captura frame de video de su cámara y lo publica en Firestore cada 600ms
  useEffect(() => {
    if (!asambleaId || !isStreaming) return; // Solo quien está transmitiendo activamente

    const canvas = document.createElement('canvas');
    canvas.width = 480;
    canvas.height = 360;
    const ctx = canvas.getContext('2d');

    const interval = setInterval(async () => {
      try {
        if (videoRef.current && videoRef.current.readyState >= 2) {
          ctx.drawImage(videoRef.current, 0, 0, 480, 360);
          const frameData = canvas.toDataURL('image/jpeg', 0.55);

          await setDoc(doc(db, `asambleas/${asambleaId}/stream_live_frame`, 'current'), {
            frame: frameData,
            broadcasterName: userData?.nombre || 'Administración',
            timestamp: Date.now()
          });
        }
      } catch (err) {
        // Silencioso
      }
    }, 600);

    return () => clearInterval(interval);
  }, [asambleaId, isStreaming, userData]);

  // Espectador: Cualquier dispositivo que NO esté transmitiendo activamente recibe los frames
  useEffect(() => {
    if (!asambleaId || isStreaming) return; // Si no estás transmitiendo, eres espectador (incluso siendo admin en móvil)

    const frameRef = doc(db, `asambleas/${asambleaId}/stream_live_frame`, 'current');
    const unsubscribe = onSnapshot(frameRef, (snap) => {
      if (snap.exists()) {
        const data = snap.data();
        if (data.timestamp && Date.now() - data.timestamp < 35000) {
          setLiveFrame(data.frame);
        } else {
          setLiveFrame(null);
        }
      }
    });

    return () => unsubscribe();
  }, [asambleaId, isStreaming]);

  // 10.1 LIVE AUDIO RELAY (Garantía de Audio Universal Inmune a Firewalls y NAT de QEMU)
  // Administrador Host: Captura fragmentos de voz autónomos y los publica en Firestore
  useEffect(() => {
    if (!asambleaId || !isStreaming || !streamRef.current || isMicMuted) return;

    const audioTracks = streamRef.current.getAudioTracks();
    if (!audioTracks || audioTracks.length === 0) return;

    const audioStream = new MediaStream(audioTracks);
    const mime = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
      ? 'audio/webm;codecs=opus'
      : MediaRecorder.isTypeSupported('audio/webm')
      ? 'audio/webm'
      : 'audio/ogg';

    let activeRecorder = null;

    const recordInterval = setInterval(() => {
      if (isMicMuted || !streamRef.current) return;
      try {
        const rec = new MediaRecorder(audioStream, { mimeType: mime });
        activeRecorder = rec;

        rec.ondataavailable = (e) => {
          if (e.data && e.data.size > 100 && !isMicMuted) {
            const reader = new FileReader();
            reader.onloadend = async () => {
              try {
                await setDoc(doc(db, `asambleas/${asambleaId}/stream_live_audio`, 'current'), {
                  audio: reader.result,
                  timestamp: Date.now()
                });
                setSentAudioChunks(prev => prev + 1);
              } catch (err) {}
            };
            reader.readAsDataURL(e.data);
          }
        };

        rec.start();
        setTimeout(() => {
          if (rec.state !== 'inactive') {
            try { rec.stop(); } catch(e) {}
          }
        }, 1100);
      } catch (e) {
        console.warn("Audio chunk notice:", e);
      }
    }, 1400);

    return () => {
      clearInterval(recordInterval);
      if (activeRecorder && activeRecorder.state !== 'inactive') {
        try { activeRecorder.stop(); } catch(e) {}
      }
    };
  }, [asambleaId, isStreaming, isMicMuted]);

  // Espectador: Recibe los fragmentos de audio y los reproduce por altavoz cuando está activo
  useEffect(() => {
    if (!asambleaId || isStreaming || isMuted) return;

    const audioDocRef = doc(db, `asambleas/${asambleaId}/stream_live_audio`, 'current');
    let lastAudioTimestamp = 0;

    const unsubscribe = onSnapshot(audioDocRef, (snap) => {
      if (snap.exists()) {
        const data = snap.data();
        if (data.audio && data.timestamp && data.timestamp > lastAudioTimestamp && (Date.now() - data.timestamp < 12000)) {
          lastAudioTimestamp = data.timestamp;
          setReceivedAudioChunks(prev => prev + 1);

          try {
            const sound = new Audio(data.audio);
            sound.volume = 1.0;
            sound.play().catch((err) => {
              console.log("Sound play error:", err);
            });
          } catch (e) {}
        }
      }
    });

    return () => unsubscribe();
  }, [asambleaId, isStreaming, isMuted]);

  // 11. WebRTC P2P Signaling: Audio y Video de alta tasa de cuadros
  // Admin Host
  useEffect(() => {
    if (!asambleaId || !isStreaming || !streamRef.current) return;

    const pc = new RTCPeerConnection(RTC_CONFIG);
    peerConnectionRef.current = pc;

    streamRef.current.getTracks().forEach(track => {
      pc.addTrack(track, streamRef.current);
    });

    pc.onicecandidate = (e) => {
      if (e.candidate) {
        addDoc(collection(db, `asambleas/${asambleaId}/webrtc_stream/admin/candidates`), e.candidate.toJSON()).catch(() => {});
      }
    };

    const initOffer = async () => {
      try {
        const offer = await pc.createOffer();
        await pc.setLocalDescription(offer);

        await setDoc(doc(db, `asambleas/${asambleaId}/webrtc_stream`, 'admin'), {
          offer: { sdp: offer.sdp, type: offer.type },
          adminUid: currentUser.uid,
          updatedAt: serverTimestamp()
        });

        // Escuchar respuestas de espectadores
        const answersCol = collection(db, `asambleas/${asambleaId}/webrtc_stream/admin/answers`);
        onSnapshot(answersCol, (snap) => {
          snap.docChanges().forEach(async (change) => {
            if (change.type === 'added') {
              const answerData = change.doc.data();
              if (answerData && answerData.answer && pc.signalingState !== 'stable') {
                try {
                  await pc.setRemoteDescription(new RTCSessionDescription(answerData.answer));
                } catch (e) {
                  console.log("Remote desc:", e);
                }
              }
            }
          });
        });
      } catch (err) {
        console.warn("WebRTC host notice:", err);
      }
    };

    initOffer();

    return () => {
      if (pc) pc.close();
    };
  }, [asambleaId, isStreaming, currentUser]);

  // Espectador Viewer (Cualquier dispositivo que no esté transmitiendo)
  useEffect(() => {
    if (!asambleaId || isStreaming || asamblea?.estatus !== 'en_vivo') return;

    const pc = new RTCPeerConnection(RTC_CONFIG);
    peerConnectionRef.current = pc;

    // Declarar explícitamente recepción de audio y video
    try {
      pc.addTransceiver('audio', { direction: 'recvonly' });
      pc.addTransceiver('video', { direction: 'recvonly' });
    } catch (e) {
      console.log("Transceivers notice:", e);
    }

    pc.ontrack = (event) => {
      if (event.streams && event.streams[0]) {
        const stream = event.streams[0];
        remoteStreamRef.current = stream;

        if (remoteVideoRef.current) {
          remoteVideoRef.current.srcObject = stream;
          remoteVideoRef.current.play().then(() => setIsPaused(false)).catch(() => setIsPaused(true));
        }

        if (remoteAudioRef.current) {
          remoteAudioRef.current.srcObject = stream;
          remoteAudioRef.current.volume = 1.0;
          remoteAudioRef.current.play().catch(() => {});
        }

        setIsRemoteVideoLive(true);
      }
    };

    const adminDocRef = doc(db, `asambleas/${asambleaId}/webrtc_stream`, 'admin');
    const unsubAdmin = onSnapshot(adminDocRef, async (snap) => {
      if (snap.exists()) {
        const data = snap.data();
        if (data && data.offer && pc.signalingState === 'stable') {
          try {
            await pc.setRemoteDescription(new RTCSessionDescription(data.offer));
            const answer = await pc.createAnswer();
            await pc.setLocalDescription(answer);

            if (currentUser) {
              await setDoc(doc(db, `asambleas/${asambleaId}/webrtc_stream/admin/answers`, currentUser.uid), {
                answer: { sdp: answer.sdp, type: answer.type },
                residenteId: currentUser.uid,
                updatedAt: serverTimestamp()
              });
            }
          } catch (e) {
            console.warn("WebRTC answer notice:", e);
          }
        }
      }
    });

    const candidatesCol = collection(db, `asambleas/${asambleaId}/webrtc_stream/admin/candidates`);
    const unsubCandidates = onSnapshot(candidatesCol, (snap) => {
      snap.docChanges().forEach(async (change) => {
        if (change.type === 'added') {
          try {
            await pc.addIceCandidate(new RTCIceCandidate(change.doc.data()));
          } catch (e) {}
        }
      });
    });

    return () => {
      unsubAdmin();
      unsubCandidates();
      if (pc) pc.close();
    };
  }, [asambleaId, isStreaming, asamblea?.estatus, currentUser]);

  // Conectar feed de video local cuando el admin activa streaming
  useEffect(() => {
    if (videoRef.current && streamRef.current) {
      videoRef.current.srcObject = streamRef.current;
    }
  }, [isStreaming]);

  // Iniciar Cámara del Administrador
  const startCamera = async (facing = 'user') => {
    try {
      setCameraError(null);
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }

      let stream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: facing, width: { ideal: 1280 }, height: { ideal: 720 } },
          audio: true
        });
      } catch (errStrict) {
        stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      }

      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play().catch(() => {});
      }
      setIsStreaming(true);
      setIsMicMuted(false);
      setIsVideoMuted(false);
    } catch (err) {
      console.warn("No se pudo iniciar la cámara web:", err);
      setCameraError(err.message || 'No se detectó cámara física');
      setIsStreaming(true);
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
    }
    setIsStreaming(false);
    setMicLevel(0);
  };

  // Toggle mano levantada para residente
  const handleToggleMano = async () => {
    if (!asambleaId || !currentUser) return;
    const manoDoc = doc(db, `asambleas/${asambleaId}/manos_levantadas`, currentUser.uid);

    try {
      if (handRaised) {
        await deleteDoc(manoDoc);
      } else {
        await setDoc(manoDoc, {
          uid: currentUser.uid,
          nombre: userData?.nombre || 'Residente',
          casa: userData?.casa || userData?.numeroCasa || 'S/N',
          createdAt: serverTimestamp()
        });
      }
    } catch (err) {
      console.error("Error toggle mano levantada:", err);
    }
  };

  // Admin baja mano de un residente
  const handleBajarManoAdmin = async (userId) => {
    if (!isAdmin || !asambleaId) return;
    try {
      await deleteDoc(doc(db, `asambleas/${asambleaId}/manos_levantadas`, userId));
    } catch (err) {
      console.error("Error bajando mano:", err);
    }
  };

  // Enviar mensaje en el chat
  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!inputMsg.trim() || !asamblea?.chatHabilitado) return;

    try {
      await addDoc(collection(db, `asambleas/${asambleaId}/chat`), {
        texto: inputMsg.trim(),
        senderName: userData.nombre || 'Residente',
        casa: userData.casa || userData.numeroCasa || 'S/N',
        rol: userData.rol || 'residente',
        createdAt: serverTimestamp()
      });
      setInputMsg('');
    } catch (err) {
      console.error("Error enviando mensaje:", err);
    }
  };

  // Toggle para silenciar el chat (Admin)
  const handleToggleChat = async () => {
    if (!isAdmin || !asamblea) return;
    try {
      await updateDoc(doc(db, 'asambleas', asambleaId), {
        chatHabilitado: !asamblea.chatHabilitado
      });
    } catch (err) {
      console.error("Error toggling chat:", err);
    }
  };

  // Lanzar Votación Flotante (60s exactos con expiraAt)
  const handleCrearVotacion = async (e) => {
    e.preventDefault();
    if (!nuevaPregunta.trim()) return;

    try {
      const nowMs = Date.now();
      await addDoc(collection(db, `asambleas/${asambleaId}/votaciones`), {
        pregunta: nuevaPregunta.trim(),
        activa: true,
        duracionSegundos: 60,
        expiraAt: nowMs + 60000,
        votosAFavor: 0,
        votosEnContra: 0,
        votosAbstencion: 0,
        createdAt: serverTimestamp()
      });
      setNuevaPregunta('');
      setShowVotacionModal(false);
    } catch (err) {
      console.error("Error creando votación:", err);
    }
  };

  // Emitir voto con indiviso y suma atómica
  const handleEmitirVoto = async (opcion) => {
    if (!activeVotacion || hasVoted) return;

    if (!tieneDerechoVoto) {
      alert("Solo los propietarios al corriente de sus cuotas pueden emitir su voto legal en esta asamblea.");
      return;
    }

    try {
      const votoRef = doc(db, `asambleas/${asambleaId}/votaciones/${activeVotacion.id}/votos`, currentUser.uid);
      await setDoc(votoRef, {
        residenteId: currentUser.uid,
        casa: userData.casa || userData.numeroCasa || 'S/N',
        nombre: userData.nombre || 'Residente',
        opcion,
        indiviso: Number(userData.indiviso) || 1.0,
        createdAt: serverTimestamp()
      });

      const fieldMap = {
        'a_favor': 'votosAFavor',
        'en_contra': 'votosEnContra',
        'abstencion': 'votosAbstencion'
      };
      const field = fieldMap[opcion] || 'votosAFavor';

      await updateDoc(doc(db, `asambleas/${asambleaId}/votaciones`, activeVotacion.id), {
        [field]: increment(1)
      });

      setHasVoted(true);
      setUserVoteOption(opcion);
    } catch (err) {
      console.error("Error emitiendo voto:", err);
      alert("No se pudo registrar tu voto: " + err.message);
    }
  };

  // Toggle de audio y reanudación de video en el teléfono
  const handleToggleAudio = () => {
    try {
      const nextMuted = !isMuted;
      setIsMuted(nextMuted);

      // 1. Desmutear y reproducir elemento <audio> dedicado
      if (remoteAudioRef.current) {
        remoteAudioRef.current.muted = nextMuted;
        remoteAudioRef.current.volume = 1.0;
        if (!nextMuted) {
          remoteAudioRef.current.play().catch(() => {});
        }
      }

      // 2. Desmutear elemento <video>
      if (remoteVideoRef.current) {
        remoteVideoRef.current.muted = nextMuted;
        if (remoteVideoRef.current.paused) {
          remoteVideoRef.current.play().then(() => setIsPaused(false)).catch(() => {});
        }
      }

      // 3. Web Audio API Bridge directo a los altavoces
      if (!nextMuted && remoteStreamRef.current) {
        const AudioCtx = window.AudioContext || window.webkitAudioContext;
        if (AudioCtx) {
          if (!spectatorAudioContextRef.current) {
            const ctx = new AudioCtx();
            spectatorAudioContextRef.current = ctx;
            const src = ctx.createMediaStreamSource(remoteStreamRef.current);
            src.connect(ctx.destination);
          } else if (spectatorAudioContextRef.current.state === 'suspended') {
            spectatorAudioContextRef.current.resume().catch(() => {});
          }
        }
      }
    } catch (err) {
      console.warn("Audio unmute notice:", err);
    }
  };

  // Concluir Asamblea y Generar Minuta Notarial con IA (Incluye TODAS las Votaciones)
  const handleFinalizarAsamblea = async () => {
    setIsFinishing(true);
    try {
      const quorumFinal = asamblea?.quorumActual || 65;
      const fechaHoy = new Date().toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric' });

      // 1. Recopilar todas las votaciones realizadas en la sesión
      const votacionesSnap = await getDocs(collection(db, `asambleas/${asambleaId}/votaciones`));
      const votacionesList = votacionesSnap.docs.map(d => ({ id: d.id, ...d.data() }));

      let resolutivosTexto = '';
      if (votacionesList.length > 0) {
        resolutivosTexto = votacionesList.map((v, i) => {
          const favor = v.votosAFavor || 0;
          const contra = v.votosEnContra || 0;
          const abst = v.votosAbstencion || 0;
          const total = favor + contra + abst;
          const aprobado = favor > contra;
          const dictamen = aprobado ? 'APROBADO POR MAYORÍA DE VOTOS' : 'RECHAZADO POR LA ASAMBLEA';

          return `Punto ${i + 1}: ${v.pregunta}\n` +
                 `   • Votos A Favor: ${favor}\n` +
                 `   • Votos En Contra: ${contra}\n` +
                 `   • Abstenciones: ${abst}\n` +
                 `   • Total Sufragios Emitidos: ${total}\n` +
                 `   • DICTAMEN LEGAL: ${dictamen}`;
        }).join('\n\n');
      } else {
        resolutivosTexto = 'No se sometieron puntos adicionales a votación digital formal durante la sesión.';
      }

      // 2. Redacción Notarial Oficial conforme a la Ley de Propiedad en Condominio
      const minutaTexto = 
`ACTA DE ASAMBLEA GENERAL DE CONDÓMINOS
CONDOMINIO: ${userData?.condominioNombre || 'RESIDENCIAL KONDIFY'}
FECHA DE CELEBRACIÓN: ${fechaHoy}

En la ciudad correspondiente, siendo la hora fijada en la convocatoria formal, se reunieron los condóminos a través de la plataforma tecnológica certificada Kondify.

I. DECLARATORIA DE QUÓRUM LEGAL:
El Presidente de Debates y el Administrador certificaron la asistencia y presencia de condóminos que representan el ${quorumFinal}% del valor total indiviso del condominio, declarándose legalmente instalada la asamblea con plenas facultades resolutivas.

II. DESAHOGO DEL ORDEN DEL DÍA:
${(asamblea?.ordenDelDia || []).map((p, i) => `${i + 1}. ${p}`).join('\n')}

III. CÓMPUTO DE VOTACIONES FORMALES Y RESOLUTIVOS:
${resolutivosTexto}

IV. DESIGNACIÓN DE DELEGADOS ESPECIALES:
Se designa a la Mesa Directiva en funciones para acudir ante Notario Público o Corredor Público para la protocolización del presente instrumento y su correspondiente inscripción en el Registro Público.

Firmado y certificado electrónicamente en la plataforma Kondify conforme al Código de Comercio y la Ley de Firma Electrónica Avanzada.`;

      // 3. Guardar en el documento de la asamblea
      await updateDoc(doc(db, 'asambleas', asambleaId), {
        estatus: 'concluida',
        quorumFinal,
        minutaTexto,
        votacionesHistorial: votacionesList,
        concluidaAt: serverTimestamp()
      });

      setVotacionesHistorialFinal(votacionesList);
      setMinutaGenerada(minutaTexto);
      stopCamera();
    } catch (err) {
      console.error("Error finalizando asamblea:", err);
      alert("Error al generar la minuta: " + err.message);
    }
    setIsFinishing(false);
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', height: '100vh', alignItems: 'center', justifyContent: 'center', background: '#0F172A', color: 'white' }}>
        Conectando a la Sala Virtual...
      </div>
    );
  }

  const totalVotos = (activeVotacion?.votosAFavor || 0) + (activeVotacion?.votosEnContra || 0) + (activeVotacion?.votosAbstencion || 0);
  const timerPercent = Math.max(0, Math.min(100, (secondsLeft / 60) * 100));

  return (
    <div style={{ background: '#0F172A', minHeight: '100vh', color: '#F8FAFC', display: 'flex', flexDirection: 'column' }}>
      {/* Banner de Sincronización de Salas en Vivo */}
      {otraAsambleaEnVivo && (
        <div style={{
          background: '#7C3AED',
          color: 'white',
          padding: '10px 16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          fontSize: '0.82rem',
          fontWeight: '700',
          boxShadow: '0 4px 12px rgba(124, 58, 237, 0.4)',
          zIndex: 100,
          gap: '10px'
        }}>
          <span>🔴 Hay otra asamblea transmitiendo en vivo: <strong>"{otraAsambleaEnVivo.titulo}"</strong></span>
          <button
            onClick={() => navigate(`/asambleas/${otraAsambleaEnVivo.id}/sala`)}
            style={{
              background: 'white',
              color: '#7C3AED',
              border: 'none',
              padding: '6px 14px',
              borderRadius: '8px',
              fontWeight: '800',
              cursor: 'pointer',
              whiteSpace: 'nowrap'
            }}
          >
            Entrar a esa Sala
          </button>
        </div>
      )}

      {/* Header Bar con Safe Area Nativo para no chocar con el reloj ni señal */}
      <header className="sala-header-safe">
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <button
            onClick={() => navigate('/asambleas')}
            style={{ background: 'rgba(255,255,255,0.1)', border: 'none', color: 'white', padding: '8px 12px', borderRadius: '10px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}
          >
            <IoArrowBack size={18} />
            <span>Salir</span>
          </button>
          <div>
            <h2 style={{ margin: 0, fontSize: '1.05rem', fontWeight: '800' }}>{asamblea?.titulo || 'Asamblea General'}</h2>
            <span style={{ fontSize: '0.75rem', color: '#94A3B8' }}>
              {asamblea?.tipo === 'extraordinaria' ? 'Asamblea Extraordinaria' : 'Asamblea Ordinaria'} • {userData?.condominioNombre || 'Condominio'}
            </span>
          </div>
        </div>

        {/* Estatus de Derecho al Voto */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          {tieneDerechoVoto ? (
            <span style={{ background: '#065F46', color: '#6EE7B7', padding: '4px 10px', borderRadius: '20px', fontSize: '0.75rem', fontWeight: '700' }}>
              ✓ Derecho a Voz y Voto
            </span>
          ) : (
            <span style={{ background: '#78350F', color: '#FCD34D', padding: '4px 10px', borderRadius: '20px', fontSize: '0.75rem', fontWeight: '700' }}>
              ⚠️ Oyente ({isMoroso ? 'Adeudo' : 'Inquilino'})
            </span>
          )}

          {isAdmin && asamblea?.estatus === 'en_vivo' && (
            <button
              onClick={handleFinalizarAsamblea}
              disabled={isFinishing}
              style={{ background: '#DC2626', color: 'white', border: 'none', borderRadius: '10px', padding: '8px 14px', fontSize: '0.8rem', fontWeight: '800', cursor: 'pointer' }}
            >
              {isFinishing ? 'Generando...' : 'Finalizar y Minuta'}
            </button>
          )}
        </div>
      </header>

      {/* Pestañas de Vista en Mobile (Para despejar la cámara y no taparla con el chat) */}
      <div className="sala-mobile-tabs">
        <button
          className={`sala-tab-btn ${mobileTab === 'video' ? 'active' : 'inactive'}`}
          onClick={() => setMobileTab('video')}
        >
          <IoVideocamOutline size={18} />
          <span>Transmisión en Vivo</span>
        </button>
        <button
          className={`sala-tab-btn ${mobileTab === 'chat' ? 'active' : 'inactive'}`}
          onClick={() => setMobileTab('chat')}
        >
          <IoChatbubblesOutline size={18} />
          <span>Chat de la Asamblea ({messages.length})</span>
        </button>
      </div>

      {/* Main Sala Layout */}
      <div className="sala-layout">
        {/* ========================================================================= */}
        {/* PANEL DE VIDEO / TRANSMISIÓN PRINCIPAL */}
        {/* ========================================================================= */}
        <div 
          className="sala-video-container"
          style={{
            display: (window.innerWidth <= 900 && mobileTab === 'chat') ? 'none' : 'flex'
          }}
        >
          {/* Barra de Quórum en tiempo real */}
          <div className="sala-quorum-bar">
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ width: '10px', height: '10px', borderRadius: '50%', background: '#EF4444', animation: 'pulseRed 2s infinite' }} />
              <span style={{ fontWeight: '800', fontSize: '0.85rem' }}>EN VIVO</span>
            </div>
            <div style={{ fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <IoPeopleOutline size={18} />
              <span>Quórum Asistencia: <strong>{asamblea?.quorumActual || 0}%</strong></span>
            </div>
          </div>

          {/* Banner de Manos Levantadas para el Administrador */}
          {isAdmin && handsList.length > 0 && (
            <div className="sala-hands-banner">
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }} onClick={() => setShowHandsDropdown(!showHandsDropdown)}>
                <IoHandRightOutline size={18} />
                <span>✋ Solicitudes de Palabra ({handsList.length}):</span>
                <strong>{handsList[0].nombre} ({handsList[0].casa})</strong>
                {handsList.length > 1 && <span style={{ opacity: 0.8 }}>+{handsList.length - 1} más</span>}
              </div>
              <button
                onClick={() => handleBajarManoAdmin(handsList[0].id || handsList[0].uid)}
                style={{ background: 'rgba(0,0,0,0.25)', border: 'none', color: 'white', borderRadius: '6px', padding: '4px 8px', fontSize: '0.75rem', fontWeight: '800', cursor: 'pointer' }}
              >
                Ceder / Bajar
              </button>
            </div>
          )}

          {/* Reproductor de Video */}
          <div style={{ flex: 1, position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#020617', minHeight: '300px' }}>
            {isStreaming ? (
              // 1. Dispositivo transmisor (Administrador con cámara activa)
              <div style={{ width: '100%', height: '100%', position: 'relative' }}>
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  className="sala-video-feed"
                />

                {/* Indicador flotante de Micrófono dinámico estilo Google Meet / Teams */}
                <div style={{
                  position: 'absolute',
                  bottom: '14px',
                  left: '14px',
                  background: isMicMuted ? 'rgba(239, 68, 68, 0.85)' : micLevel > 8 ? 'rgba(34, 197, 94, 0.9)' : 'rgba(15, 23, 42, 0.75)',
                  backdropFilter: 'blur(6px)',
                  color: 'white',
                  padding: '6px 12px',
                  borderRadius: '20px',
                  fontSize: '0.75rem',
                  fontWeight: '800',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  boxShadow: (!isMicMuted && micLevel > 8) ? '0 0 16px rgba(34, 197, 94, 0.7)' : 'none',
                  transition: 'all 0.15s ease'
                }}>
                  {isMicMuted ? <IoMicOffOutline size={16} /> : <IoMicOutline size={16} />}
                  <span>{isMicMuted ? 'Micrófono Muteado' : micLevel > 8 ? '🎙️ Transmitiendo Voz' : 'Micrófono Listo'}</span>
                  {sentAudioChunks > 0 && (
                    <span style={{ fontSize: '0.68rem', color: '#86EFAC', marginLeft: '4px', background: 'rgba(0,0,0,0.3)', padding: '2px 6px', borderRadius: '10px' }}>
                      {sentAudioChunks} paq.
                    </span>
                  )}
                  {!isMicMuted && (
                    <div style={{ display: 'flex', alignItems: 'flex-end', gap: '2px', height: '12px' }}>
                      <span style={{ width: '3px', height: `${Math.max(3, micLevel * 0.12)}px`, background: 'white', borderRadius: '1px', transition: 'height 0.08s ease' }} />
                      <span style={{ width: '3px', height: `${Math.max(3, micLevel * 0.16)}px`, background: 'white', borderRadius: '1px', transition: 'height 0.08s ease' }} />
                      <span style={{ width: '3px', height: `${Math.max(3, micLevel * 0.10)}px`, background: 'white', borderRadius: '1px', transition: 'height 0.08s ease' }} />
                    </div>
                  )}
                </div>
              </div>
            ) : asamblea?.estatus === 'en_vivo' ? (
              // 2. Dispositivo espectador (Cualquier usuario o móvil que esté viendo la asamblea)
              <div 
                style={{ width: '100%', height: '100%', position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', overflow: 'hidden' }}
                onClick={handleToggleAudio}
              >
                {/* Elemento de Audio Dedicado para salida limpia de alta fidelidad sin restricciones de CSS */}
                <audio
                  ref={remoteAudioRef}
                  autoPlay
                  playsInline
                  muted={isMuted}
                  style={{ position: 'absolute', width: '1px', height: '1px', opacity: 0.01, pointerEvents: 'none' }}
                />

                {/* Video WebRTC en segundo plano */}
                <video
                  ref={remoteVideoRef}
                  autoPlay
                  playsInline
                  muted={isMuted}
                  onPlay={() => setIsPaused(false)}
                  onPause={() => setIsPaused(true)}
                  className="sala-video-feed"
                  style={{
                    display: (!liveFrame && isRemoteVideoLive) ? 'block' : 'none',
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover'
                  }}
                />

                {/* Live Frame Relay: Imagen real de la cámara de la presidencia en tiempo real */}
                {liveFrame && (
                  <div style={{ width: '100%', height: '100%', position: 'relative' }}>
                    <img
                      src={liveFrame}
                      alt="Transmisión en Vivo de la Presidencia"
                      className="sala-video-feed"
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                    <div style={{
                      position: 'absolute',
                      bottom: '14px',
                      left: '14px',
                      background: 'rgba(15, 23, 42, 0.85)',
                      backdropFilter: 'blur(8px)',
                      color: '#4ADE80',
                      fontSize: '0.75rem',
                      fontWeight: '800',
                      padding: '6px 12px',
                      borderRadius: '20px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
                      zIndex: 20
                    }}>
                      <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#22C55E', animation: 'pulseRed 1.5s infinite' }} />
                      <span>🔴 Transmisión de la Presidencia en Vivo</span>
                      {receivedAudioChunks > 0 && (
                        <span style={{ fontSize: '0.68rem', color: '#86EFAC', background: 'rgba(0,0,0,0.35)', padding: '2px 6px', borderRadius: '10px' }}>
                          • {receivedAudioChunks} paq. audio
                        </span>
                      )}
                    </div>
                  </div>
                )}

                {/* Botón flotante siempre visible para activar/desactivar Audio en el teléfono */}
                {(isRemoteVideoLive || liveFrame) && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleToggleAudio();
                    }}
                    style={{
                      position: 'absolute',
                      bottom: '14px',
                      right: '14px',
                      background: isMuted ? 'rgba(239, 68, 68, 0.9)' : 'rgba(22, 163, 74, 0.9)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '20px',
                      padding: '8px 16px',
                      fontSize: '0.78rem',
                      fontWeight: '800',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      boxShadow: '0 4px 14px rgba(0,0,0,0.5)',
                      zIndex: 35,
                      backdropFilter: 'blur(6px)'
                    }}
                  >
                    {isMuted ? <IoVolumeMuteOutline size={18} /> : <IoVolumeHighOutline size={18} />}
                    <span>{isMuted ? '🔇 Toca para Activar Audio' : '🔊 Audio en Vivo'}</span>
                  </button>
                )}

                {/* Overlay si el video fue pausado por el sistema */}
                {(!liveFrame && isRemoteVideoLive && isPaused) && (
                  <div style={{
                    position: 'absolute',
                    inset: 0,
                    background: 'rgba(15, 23, 42, 0.75)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 30
                  }}>
                    <div style={{
                      width: '64px',
                      height: '64px',
                      borderRadius: '50%',
                      background: '#7E66FC',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginBottom: '10px',
                      boxShadow: '0 0 24px rgba(126, 102, 252, 0.6)',
                      fontSize: '24px'
                    }}>
                      ▶
                    </div>
                    <span style={{ color: 'white', fontWeight: '800', fontSize: '0.88rem' }}>
                      Toca la pantalla para reproducir en vivo
                    </span>
                  </div>
                )}

                {!isRemoteVideoLive && !liveFrame && (
                  <div style={{ textAlign: 'center', padding: '28px' }}>
                    <div style={{
                      width: '76px',
                      height: '76px',
                      borderRadius: '50%',
                      background: 'rgba(126, 102, 252, 0.15)',
                      color: '#7E66FC',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto 16px',
                      boxShadow: '0 0 0 10px rgba(126, 102, 252, 0.08)'
                    }}>
                      <IoVideocamOutline size={44} />
                    </div>
                    <h3 style={{ margin: '0 0 6px', color: '#F1F5F9', fontWeight: '800', fontSize: '1.15rem' }}>
                      🔴 Transmisión de la Mesa Directiva
                    </h3>
                    <p style={{ margin: 0, fontSize: '0.85rem', color: '#94A3B8' }}>
                      Esperando que la administración inicie el stream de video...
                    </p>

                    {isAdmin && (
                      <button
                        onClick={() => startCamera(cameraFacing)}
                        style={{ marginTop: '16px', background: '#7E66FC', color: 'white', border: 'none', borderRadius: '12px', padding: '10px 20px', fontWeight: '800', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '8px' }}
                      >
                        <IoVideocamOutline size={18} />
                        <span>Transmitir desde este Dispositivo</span>
                      </button>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '24px' }}>
                <IoVideocamOffOutline size={56} style={{ color: '#475569', marginBottom: '12px' }} />
                <h3 style={{ margin: '0 0 6px', color: '#94A3B8' }}>
                  La sala virtual está esperando el inicio de sesión.
                </h3>
              </div>
            )}
          </div>

          {/* Barra de Controles Inferior */}
          <div className="sala-controls-bar">
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              {isAdmin ? (
                <>
                  {/* Botón de Micrófono con VU Meter en tiempo real */}
                  <button
                    onClick={() => {
                      if (streamRef.current) {
                        const track = streamRef.current.getAudioTracks()[0];
                        if (track) track.enabled = !track.enabled;
                        setIsMicMuted(!isMicMuted);
                      }
                    }}
                    title={isMicMuted ? "Activar Micrófono" : "Silenciar Micrófono"}
                    style={{
                      background: isMicMuted ? '#DC2626' : micLevel > 8 ? '#16A34A' : 'rgba(255,255,255,0.1)',
                      border: 'none',
                      color: 'white',
                      padding: '8px 12px',
                      borderRadius: '10px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      boxShadow: (!isMicMuted && micLevel > 8) ? '0 0 14px rgba(34, 197, 94, 0.7)' : 'none',
                      transition: 'all 0.12s ease'
                    }}
                  >
                    {isMicMuted ? <IoMicOffOutline size={18} /> : <IoMicOutline size={18} />}
                    {!isMicMuted && (
                      <div style={{ display: 'flex', alignItems: 'flex-end', gap: '2px', height: '12px' }}>
                        <span style={{ width: '2px', height: `${Math.max(2, micLevel * 0.12)}px`, background: 'white', borderRadius: '1px' }} />
                        <span style={{ width: '2px', height: `${Math.max(2, micLevel * 0.16)}px`, background: 'white', borderRadius: '1px' }} />
                        <span style={{ width: '2px', height: `${Math.max(2, micLevel * 0.10)}px`, background: 'white', borderRadius: '1px' }} />
                      </div>
                    )}
                  </button>

                  <button
                    onClick={() => {
                      if (!isStreaming) {
                        startCamera(cameraFacing);
                      } else if (streamRef.current) {
                        const track = streamRef.current.getVideoTracks()[0];
                        if (track) track.enabled = !track.enabled;
                        setIsVideoMuted(!isVideoMuted);
                      }
                    }}
                    title={isVideoMuted ? "Activar Cámara" : "Apagar Cámara"}
                    style={{ background: isVideoMuted ? '#DC2626' : 'rgba(255,255,255,0.1)', border: 'none', color: 'white', padding: '8px 12px', borderRadius: '10px', cursor: 'pointer' }}
                  >
                    {isVideoMuted ? <IoVideocamOffOutline size={18} /> : <IoVideocamOutline size={18} />}
                  </button>

                  <button
                    onClick={() => {
                      const next = cameraFacing === 'user' ? 'environment' : 'user';
                      setCameraFacing(next);
                      startCamera(next);
                    }}
                    title="Alternar Cámara Frontal / Trasera"
                    style={{ background: 'rgba(255,255,255,0.1)', border: 'none', color: 'white', padding: '8px 12px', borderRadius: '10px', cursor: 'pointer' }}
                  >
                    <IoRefreshOutline size={18} />
                  </button>
                </>
              ) : (
                <button
                  onClick={handleToggleMano}
                  style={{
                    background: handRaised ? '#F59E0B' : 'rgba(255,255,255,0.1)',
                    border: 'none',
                    color: 'white',
                    padding: '8px 14px',
                    borderRadius: '10px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    fontWeight: '700',
                    fontSize: '0.85rem',
                    transition: 'all 0.2s'
                  }}
                >
                  <IoHandRightOutline size={18} />
                  <span>{handRaised ? '✋ Mano Levantada' : 'Pedir la Palabra'}</span>
                </button>
              )}
            </div>

            {/* Controles de Admin (Lanzar Votación y Silenciar Chat) */}
            {isAdmin && (
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  onClick={handleToggleChat}
                  title={asamblea?.chatHabilitado ? 'Silenciar Chat para todos' : 'Habilitar Chat'}
                  style={{
                    background: asamblea?.chatHabilitado ? 'rgba(255,255,255,0.1)' : '#EF4444',
                    border: 'none',
                    color: 'white',
                    padding: '8px 12px',
                    borderRadius: '10px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    fontSize: '0.8rem',
                    fontWeight: '700'
                  }}
                >
                  {asamblea?.chatHabilitado ? <IoLockOpenOutline size={16} /> : <IoLockClosedOutline size={16} />}
                  <span>{asamblea?.chatHabilitado ? 'Chat Abierto' : 'Silenciado'}</span>
                </button>

                <button
                  onClick={() => setShowVotacionModal(true)}
                  style={{ background: '#7E66FC', color: 'white', border: 'none', borderRadius: '10px', padding: '8px 14px', fontSize: '0.8rem', fontWeight: '800', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}
                >
                  <IoStatsChartOutline size={16} />
                  <span>+ Votación</span>
                </button>
              </div>
            )}
          </div>
        </div>

        {/* ========================================================================= */}
        {/* PANEL DE CHAT MODERADO */}
        {/* ========================================================================= */}
        <div 
          className="sala-chat-panel"
          style={{
            display: (window.innerWidth <= 900 && mobileTab === 'video') ? 'none' : 'flex'
          }}
        >
          <div className="sala-chat-header">
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <IoChatbubblesOutline size={18} color="#7E66FC" />
              <span style={{ fontWeight: '800', fontSize: '0.9rem', color: '#0F172A' }}>Chat de la Asamblea</span>
            </div>
            {!asamblea?.chatHabilitado && (
              <span style={{ fontSize: '0.7rem', background: '#FEE2E2', color: '#DC2626', padding: '2px 8px', borderRadius: '12px', fontWeight: '700' }}>
                Silenciado
              </span>
            )}
          </div>

          <div className="sala-chat-messages">
            {messages.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '30px 10px', color: '#94A3B8', fontSize: '0.85rem' }}>
                No hay comentarios aún. Usa este chat con respeto y apego al orden del día.
              </div>
            ) : (
              messages.map(m => (
                <div key={m.id} className="sala-chat-bubble">
                  <div className="sender">
                    {m.senderName} ({m.casa})
                  </div>
                  <div style={{ color: '#1E293B' }}>{m.texto}</div>
                </div>
              ))
            )}
          </div>

          {asamblea?.chatHabilitado ? (
            <form onSubmit={handleSendMessage} className="sala-chat-input-row">
              <input
                type="text"
                placeholder="Escribe tu duda u opinión..."
                value={inputMsg}
                onChange={(e) => setInputMsg(e.target.value)}
                className="sala-chat-input"
              />
              <button
                type="submit"
                style={{ background: '#7E66FC', color: 'white', border: 'none', borderRadius: '10px', padding: '8px 12px', cursor: 'pointer' }}
              >
                <IoSend size={16} />
              </button>
            </form>
          ) : (
            <div style={{ padding: '12px', textAlign: 'center', fontSize: '0.75rem', color: '#94A3B8', background: '#F8FAFC' }}>
              🔒 El chat ha sido silenciado por la Mesa Directiva.
            </div>
          )}
        </div>
      </div>

      {/* ========================================================================= */}
      {/* MODAL GLOBAL DE VOTACIÓN EN VIVO (Flotante, Centrado, NUNCA se corta) */}
      {/* ========================================================================= */}
      {activeVotacion && (
        <div className="sala-vote-modal-backdrop">
          <div className="sala-vote-modal-card">
            {/* Header Votación */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{
                background: '#EEF2FF',
                color: '#4F46E5',
                border: '1px solid #C7D2FE',
                padding: '4px 10px',
                borderRadius: '8px',
                fontSize: '0.75rem',
                fontWeight: '800',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}>
                <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#4F46E5', animation: 'pulseRed 1.5s infinite' }} />
                VOTACIÓN EN VIVO ({secondsLeft}s)
              </span>

              {hasVoted ? (
                <span style={{ fontSize: '0.8rem', color: '#059669', fontWeight: '800', display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <IoCheckmark size={16} /> Voto Recibido
                </span>
              ) : (
                <span style={{ fontSize: '0.8rem', color: '#64748B', fontWeight: '700' }}>
                  Selecciona tu postura
                </span>
              )}
            </div>

            {/* Barra de Tiempo Regresivo */}
            <div className="sala-vote-timer-bar-wrap">
              <div className="sala-vote-timer-bar-fill" style={{ width: `${timerPercent}%` }} />
            </div>

            {/* Pregunta */}
            <h3 style={{ margin: '0 0 16px', fontSize: '1.2rem', fontWeight: '800', lineHeight: '1.4', color: '#0F172A' }}>
              {activeVotacion.pregunta}
            </h3>

            {/* Botones o Confirmación */}
            {tieneDerechoVoto ? (
              hasVoted ? (
                <div style={{
                  background: '#F0FDF4',
                  border: '1px solid #BBF7D0',
                  borderRadius: '16px',
                  padding: '16px',
                  textAlign: 'center',
                  marginTop: '12px'
                }}>
                  <div style={{
                    width: '44px',
                    height: '44px',
                    borderRadius: '50%',
                    background: '#10B981',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 8px'
                  }}>
                    <IoCheckmark size={26} />
                  </div>
                  <h4 style={{ margin: '0 0 4px', color: '#065F46', fontWeight: '800' }}>
                    Tu voto ha sido registrado
                  </h4>
                  <p style={{ margin: 0, fontSize: '0.85rem', color: '#047857' }}>
                    Postura emitida: <strong>{userVoteOption === 'a_favor' ? 'A Favor' : userVoteOption === 'en_contra' ? 'En Contra' : 'Abstención'}</strong>
                  </p>
                  <p style={{ margin: '4px 0 0', fontSize: '0.75rem', color: '#64748B' }}>
                    Computado con el porcentaje indiviso de tu propiedad.
                  </p>
                </div>
              ) : (
                <div className="sala-vote-btn-grid">
                  <button
                    onClick={() => handleEmitirVoto('a_favor')}
                    className="sala-vote-btn favor"
                  >
                    <span>👍</span>
                    <span>A Favor</span>
                  </button>

                  <button
                    onClick={() => handleEmitirVoto('en_contra')}
                    className="sala-vote-btn contra"
                  >
                    <span>👎</span>
                    <span>En Contra</span>
                  </button>

                  <button
                    onClick={() => handleEmitirVoto('abstencion')}
                    className="sala-vote-btn abstencion"
                  >
                    <span>✋</span>
                    <span>Abstención</span>
                  </button>
                </div>
              )
            ) : (
              <div style={{ background: '#FEF3C7', border: '1px solid #FDE68A', padding: '14px', borderRadius: '14px', color: '#92400E', fontSize: '0.85rem', lineHeight: '1.4' }}>
                ⚠️ <strong>Modo Oyente:</strong> Solo los propietarios al corriente en sus cuotas tienen derecho a emitir su voto en este punto legal de la asamblea.
              </div>
            )}

            {/* Resultados en tiempo real para el Administrador */}
            {isAdmin && (
              <div style={{ marginTop: '18px', paddingTop: '14px', borderTop: '1px solid #E2E8F0', fontSize: '0.8rem', color: '#475569' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px', fontWeight: '700' }}>
                  <span>Cómputo en vivo ({totalVotos} votos):</span>
                  <span>A Favor: {activeVotacion.votosAFavor || 0} | En Contra: {activeVotacion.votosEnContra || 0}</span>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modal para que el Admin cree una Votación */}
      {showVotacionModal && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(15, 23, 42, 0.75)',
          backdropFilter: 'blur(6px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000,
          padding: '16px'
        }}>
          <div style={{ background: 'white', borderRadius: '24px', padding: '24px', maxWidth: '480px', width: '100%', color: '#0F172A', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.3)' }}>
            <h3 style={{ margin: '0 0 10px', fontWeight: '800' }}>Lanzar Votación en Vivo</h3>
            <p style={{ margin: '0 0 16px', fontSize: '0.85rem', color: '#64748B' }}>
              Se desplegará una ventana emergente de 60 segundos fijos en las pantallas de todos los condóminos con derecho a voto.
            </p>

            <form onSubmit={handleCrearVotacion}>
              <textarea
                required
                rows={3}
                placeholder="Ej. ¿Se aprueba el presupuesto anual de mantenimiento para el ejercicio 2026?"
                value={nuevaPregunta}
                onChange={(e) => setNuevaPregunta(e.target.value)}
                style={{ width: '100%', padding: '12px', borderRadius: '12px', border: '1px solid #CBD5E1', outline: 'none', boxSizing: 'border-box', marginBottom: '16px', fontSize: '0.9rem' }}
              />

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                <button
                  type="button"
                  onClick={() => setShowVotacionModal(false)}
                  style={{ background: '#F1F5F9', border: 'none', borderRadius: '10px', padding: '10px 18px', fontWeight: '700', cursor: 'pointer' }}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  style={{ background: '#7E66FC', color: 'white', border: 'none', borderRadius: '10px', padding: '10px 20px', fontWeight: '800', cursor: 'pointer' }}
                >
                  Lanzar Votación (60s)
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de Minuta Concluida Oficial con IA y Votaciones */}
      {minutaGenerada && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(15, 23, 42, 0.85)',
          backdropFilter: 'blur(8px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000,
          padding: '16px'
        }}>
          <div style={{ background: 'white', borderRadius: '24px', padding: '28px', maxWidth: '680px', width: '100%', color: '#0F172A', maxHeight: '88vh', overflowY: 'auto' }}>
            <div style={{ textAlign: 'center', marginBottom: '16px' }}>
              <IoCheckmarkCircleOutline size={52} color="#16A34A" />
              <h2 style={{ margin: '8px 0 4px', fontWeight: '800' }}>¡Asamblea Concluida con Validez Legal!</h2>
              <p style={{ margin: 0, fontSize: '0.85rem', color: '#64748B' }}>
                La Inteligencia Artificial ha certificado el Quórum e integrado el cómputo oficial de todas las votaciones formales.
              </p>
            </div>

            {/* Desglose de Votaciones */}
            {votacionesHistorialFinal.length > 0 && (
              <div style={{ marginBottom: '16px', background: '#F1F5F9', padding: '14px', borderRadius: '14px' }}>
                <h4 style={{ margin: '0 0 10px', fontSize: '0.88rem', color: '#1E293B', fontWeight: '800' }}>
                  📊 Votaciones Digitales Registradas ({votacionesHistorialFinal.length}):
                </h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {votacionesHistorialFinal.map((v, i) => (
                    <div key={i} style={{ background: 'white', padding: '10px 14px', borderRadius: '10px', fontSize: '0.8rem', borderLeft: (v.votosAFavor > v.votosEnContra) ? '4px solid #10B981' : '4px solid #EF4444' }}>
                      <strong>{v.pregunta}</strong>
                      <div style={{ display: 'flex', gap: '14px', marginTop: '4px', color: '#64748B' }}>
                        <span style={{ color: '#059669', fontWeight: '700' }}>👍 A Favor: {v.votosAFavor || 0}</span>
                        <span style={{ color: '#DC2626', fontWeight: '700' }}>👎 En Contra: {v.votosEnContra || 0}</span>
                        <span>✋ Abstención: {v.votosAbstencion || 0}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div style={{ background: '#F8FAFC', border: '1px solid #E2E8F0', padding: '16px', borderRadius: '12px', whiteSpace: 'pre-wrap', fontSize: '0.82rem', lineHeight: '1.6', maxHeight: '220px', overflowY: 'auto', marginBottom: '20px' }}>
              {minutaGenerada}
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button
                onClick={() => navigate('/asambleas')}
                style={{ background: '#0F172A', color: 'white', border: 'none', borderRadius: '12px', padding: '12px 24px', fontWeight: '800', cursor: 'pointer' }}
              >
                Cerrar y Volver a Asambleas
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SalaAsambleaScreen;
